import { useRef, useState } from "react";
import { Camera, Star, X } from "@phosphor-icons/react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";

/**
 * Multi-photo picker for project creation.
 * - Accepts up to `max` uploaded photo objects (default 10).
 * - First photo doubles as the AI-vision cover (front-end ensures it's first in the array).
 * - Each thumbnail has a × badge to remove.
 *
 * Props:
 *   value:      string[]  — current array of upload ids (controlled)
 *   onChange:   (ids) => void
 *   max:        number    — defaults to 10
 *   disabled:   boolean
 *   testidBase: string    — e.g. "new-photo"  → tiles get `${testidBase}-tile-N` etc.
 */
export default function MultiPhotoPicker({ value = [], onChange, max = 10, disabled = false, testidBase = "multi-photo" }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const ids = value;
  const remaining = Math.max(0, max - ids.length);
  const coverId = ids[0];

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const remainingSlots = Math.max(0, max - ids.length);
    if (remainingSlots === 0) {
      toast.error(`You can only add up to ${max} photos.`);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    const toUpload = files.slice(0, remainingSlots);
    if (files.length > remainingSlots) {
      toast.warning(`Only the first ${remainingSlots} photo(s) were added (max ${max}).`);
    }
    setUploading(true);
    const newIds = [...ids];
    try {
      for (const file of toUpload) {
        if (file.size > 12 * 1024 * 1024) {
          toast.error(`${file.name || "Image"} is too large (12MB max)`);
          continue;
        }
        const form = new FormData();
        form.append("file", file);
        try {
          const r = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
          newIds.push(r.data.id);
          onChange?.([...newIds]);
        } catch (err) {
          toast.error(err?.response?.data?.detail || `Upload failed for ${file.name || "image"}`);
        }
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeAt = (idx) => {
    const next = ids.filter((_, i) => i !== idx);
    onChange?.(next);
  };

  const promoteToCover = (idx) => {
    if (idx === 0) return;
    const next = [ids[idx], ...ids.filter((_, i) => i !== idx)];
    onChange?.(next);
  };

  return (
    <div data-testid={testidBase}>
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[11px] font-bold uppercase tracking-wider text-timber-700">
          Photos · {ids.length}/{max}
        </p>
        {ids.length > 1 && (
          <p className="text-[10px] text-timber-500">Tap to set cover · AI uses up to 5</p>
        )}
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 snap-x">
        {ids.map((pid, idx) => (
          <div
            key={pid}
            data-testid={`${testidBase}-tile-${idx}`}
            className={`relative flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden border-2 snap-start ${
              pid === coverId ? "border-masking shadow-tactile-orange" : "border-stone_border"
            }`}
          >
            <button type="button" onClick={() => promoteToCover(idx)} className="w-full h-full">
              <img src={fileUrl(pid)} alt="" className="w-full h-full object-cover" />
            </button>
            {idx === 0 && (
              <span className="absolute top-1 left-1 bg-masking text-timber-900 text-[10px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5 pointer-events-none">
                <Star size={10} weight="fill" /> Cover
              </span>
            )}
            <button
              type="button"
              data-testid={`${testidBase}-remove-${idx}`}
              onClick={() => removeAt(idx)}
              className="absolute -top-1.5 -right-1.5 bg-block text-white rounded-full w-5 h-5 flex items-center justify-center"
              aria-label="Remove photo"
            >
              <X size={11} weight="bold" />
            </button>
            {idx >= 5 && (
              <span className="absolute bottom-1 left-1 bg-timber-900/80 text-chalk text-[9px] font-bold px-1.5 py-0.5 rounded-full pointer-events-none">
                extra
              </span>
            )}
          </div>
        ))}
        {remaining > 0 && (
          <button
            type="button"
            data-testid={`${testidBase}-add`}
            onClick={() => fileRef.current?.click()}
            disabled={disabled || uploading}
            className="flex-shrink-0 w-24 h-24 rounded-xl border-2 border-dashed border-stone_border bg-kraft flex flex-col items-center justify-center gap-1 text-timber-700 hover:border-masking hover:text-masking transition snap-start"
          >
            <Camera size={22} weight="duotone" />
            <span className="text-[10px] font-bold uppercase tracking-wider">
              {uploading ? "Saving…" : "Add"}
            </span>
          </button>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        onChange={upload}
        className="hidden"
        data-testid={`${testidBase}-input`}
      />
    </div>
  );
}
